import { useState, useRef, useEffect, useCallback } from "react";
import { Textarea } from "@/components/ui/textarea";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuthenticatedUpload } from "@/hooks/useAuthenticatedUpload";
import { toast } from "sonner";
import { 
  Eye, 
  Edit3, 
  Bold, 
  Italic, 
  List, 
  ListOrdered, 
  Code, 
  Link2, 
  Heading1, 
  Heading2,
  Quote,
  Minus,
  FileCode,
  ChevronDown,
  ImagePlus,
  Loader2,
  Upload,
  Undo2,
  Redo2,
  Save,
  Trash2,
  Maximize2,
  Minimize2,
  Table,
  Smile,
  Search,
  Replace,
  X,
  ChevronUp
} from "lucide-react";
import { Input } from "@/components/ui/input";

const emojiCategories = [
  { 
    name: "Smileys", 
    emojis: ["😀", "😃", "😄", "😁", "😅", "😂", "🤣", "😊", "😇", "🙂", "😉", "😍", "🥰", "😘", "😎", "🤩", "🥳", "😏", "😢", "😭", "😤", "🤔", "🤨", "😐", "😑", "🙄", "😬", "🤫", "🤭", "🤗", "🤯", "😱"]
  },
  { 
    name: "Gestures", 
    emojis: ["👍", "👎", "👌", "✌️", "🤞", "🤟", "🤘", "🤙", "👈", "👉", "👆", "👇", "☝️", "✋", "🤚", "🖐️", "🖖", "👋", "🤝", "👏", "🙌", "👐", "🤲", "🙏", "✍️", "💪", "🦾", "🦿"]
  },
  { 
    name: "Objects", 
    emojis: ["💡", "🔥", "⭐", "✨", "🎉", "🎊", "🏆", "🥇", "🎯", "💯", "✅", "❌", "⚡", "💎", "🔔", "📌", "📍", "🔗", "💼", "📁", "📂", "📝", "✏️", "📊", "📈", "📉", "🔒", "🔓", "🔑", "⚙️", "🛠️", "💻"]
  },
  { 
    name: "Symbols", 
    emojis: ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "💔", "❣️", "💕", "💖", "💗", "💘", "💝", "⚠️", "🚫", "⛔", "📢", "💬", "💭", "🗨️", "➡️", "⬅️", "⬆️", "⬇️", "↗️", "↘️", "↙️", "↖️", "🔄", "ℹ️"]
  }
];

const codeLanguages = [
  { value: "javascript", label: "JavaScript" },
  { value: "typescript", label: "TypeScript" },
  { value: "python", label: "Python" },
  { value: "java", label: "Java" },
  { value: "csharp", label: "C#" },
  { value: "cpp", label: "C++" },
  { value: "go", label: "Go" },
  { value: "rust", label: "Rust" },
  { value: "ruby", label: "Ruby" },
  { value: "php", label: "PHP" },
  { value: "swift", label: "Swift" },
  { value: "kotlin", label: "Kotlin" },
  { value: "sql", label: "SQL" },
  { value: "html", label: "HTML" },
  { value: "css", label: "CSS" },
  { value: "json", label: "JSON" },
  { value: "yaml", label: "YAML" },
  { value: "bash", label: "Bash" },
  { value: "markdown", label: "Markdown" },
  { value: "text", label: "Plain Text" },
];

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  id?: string;
  storageKey?: string; // Optional key for autosave to localStorage
}

export const MarkdownEditor = ({ 
  value, 
  onChange, 
  placeholder = "Write your description using markdown...",
  rows = 6,
  id,
  storageKey
}: MarkdownEditorProps) => {
  const [activeTab, setActiveTab] = useState<"write" | "preview">("write");
  const [showCodeDropdown, setShowCodeDropdown] = useState(false);
  const [showTableGrid, setShowTableGrid] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiCategory, setEmojiCategory] = useState(0);
  const [tableGridHover, setTableGridHover] = useState({ rows: 0, cols: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const { uploadFile, handleError } = useAuthenticatedUpload({ 
    bucket: 'challenge-attachments' 
  });
  const [localIsUploading, setLocalIsUploading] = useState(false);
  
  // Find and replace state
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [matchCount, setMatchCount] = useState(0);
  
  // History tracking for undo/redo
  const [history, setHistory] = useState<string[]>([value]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const isUndoRedoRef = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tableGridRef = useRef<HTMLDivElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const findInputRef = useRef<HTMLInputElement>(null);

  // Calculate matches when find text changes
  useEffect(() => {
    if (!findText) {
      setMatchCount(0);
      setCurrentMatchIndex(0);
      return;
    }
    
    const regex = new RegExp(findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    const matches = value.match(regex);
    setMatchCount(matches?.length || 0);
    if (currentMatchIndex >= (matches?.length || 0)) {
      setCurrentMatchIndex(0);
    }
  }, [findText, value]);

  // Focus find input when opening
  useEffect(() => {
    if (showFindReplace && findInputRef.current) {
      findInputRef.current.focus();
    }
  }, [showFindReplace]);

  const findNext = useCallback(() => {
    if (!findText || matchCount === 0) return;
    
    const newIndex = (currentMatchIndex + 1) % matchCount;
    setCurrentMatchIndex(newIndex);
    
    // Find and select the match in textarea
    const textarea = document.getElementById(id || "markdown-editor") as HTMLTextAreaElement;
    if (!textarea) return;
    
    const regex = new RegExp(findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    let match;
    let count = 0;
    while ((match = regex.exec(value)) !== null) {
      if (count === newIndex) {
        textarea.focus();
        textarea.setSelectionRange(match.index, match.index + findText.length);
        break;
      }
      count++;
    }
  }, [findText, matchCount, currentMatchIndex, value, id]);

  const findPrevious = useCallback(() => {
    if (!findText || matchCount === 0) return;
    
    const newIndex = currentMatchIndex === 0 ? matchCount - 1 : currentMatchIndex - 1;
    setCurrentMatchIndex(newIndex);
    
    const textarea = document.getElementById(id || "markdown-editor") as HTMLTextAreaElement;
    if (!textarea) return;
    
    const regex = new RegExp(findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    let match;
    let count = 0;
    while ((match = regex.exec(value)) !== null) {
      if (count === newIndex) {
        textarea.focus();
        textarea.setSelectionRange(match.index, match.index + findText.length);
        break;
      }
      count++;
    }
  }, [findText, matchCount, currentMatchIndex, value, id]);

  const replaceCurrent = useCallback(() => {
    if (!findText || matchCount === 0) return;
    
    const regex = new RegExp(findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    let match;
    let count = 0;
    while ((match = regex.exec(value)) !== null) {
      if (count === currentMatchIndex) {
        const newValue = value.substring(0, match.index) + replaceText + value.substring(match.index + findText.length);
        onChange(newValue);
        break;
      }
      count++;
    }
  }, [findText, replaceText, matchCount, currentMatchIndex, value, onChange]);

  const replaceAll = useCallback(() => {
    if (!findText) return;
    
    const regex = new RegExp(findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    const newValue = value.replace(regex, replaceText);
    onChange(newValue);
    toast.success(`Replaced ${matchCount} occurrence(s)`);
  }, [findText, replaceText, value, onChange, matchCount]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCodeDropdown(false);
      }
      if (tableGridRef.current && !tableGridRef.current.contains(event.target as Node)) {
        setShowTableGrid(false);
      }
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Insert emoji at cursor
  const insertEmoji = useCallback((emoji: string) => {
    const textarea = document.getElementById(id || "markdown-editor") as HTMLTextAreaElement;
    if (!textarea) {
      onChange(value + emoji);
    } else {
      const start = textarea.selectionStart;
      const newText = value.substring(0, start) + emoji + value.substring(start);
      onChange(newText);
      
      setTimeout(() => {
        textarea.focus();
        const cursorPos = start + emoji.length;
        textarea.setSelectionRange(cursorPos, cursorPos);
      }, 0);
    }
  }, [id, value, onChange]);

  // Generate markdown table
  const insertTable = useCallback((rows: number, cols: number) => {
    const header = "| " + Array(cols).fill("Header").map((h, i) => `${h} ${i + 1}`).join(" | ") + " |";
    const separator = "| " + Array(cols).fill("---").join(" | ") + " |";
    const bodyRows = Array(rows - 1).fill(null).map(() => 
      "| " + Array(cols).fill("Cell").join(" | ") + " |"
    ).join("\n");
    
    const table = `\n${header}\n${separator}\n${bodyRows}\n`;
    
    const textarea = document.getElementById(id || "markdown-editor") as HTMLTextAreaElement;
    if (!textarea) {
      onChange(value + table);
    } else {
      const start = textarea.selectionStart;
      const newText = value.substring(0, start) + table + value.substring(start);
      onChange(newText);
    }
    
    setShowTableGrid(false);
    setTableGridHover({ rows: 0, cols: 0 });
  }, [id, value, onChange]);

  // Load draft from localStorage on mount
  useEffect(() => {
    if (!storageKey) return;
    
    const savedDraft = localStorage.getItem(`markdown-draft-${storageKey}`);
    if (savedDraft) {
      try {
        const { content, savedAt } = JSON.parse(savedDraft);
        if (content && content !== value) {
          setHasDraft(true);
        }
      } catch (e) {
        // Invalid JSON, ignore
      }
    }
  }, [storageKey]);

  // Autosave to localStorage with debounce
  useEffect(() => {
    if (!storageKey || !value) return;
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      const draft = JSON.stringify({
        content: value,
        savedAt: new Date().toISOString()
      });
      localStorage.setItem(`markdown-draft-${storageKey}`, draft);
      setLastSaved(new Date());
      setHasDraft(false);
    }, 1000); // Save after 1 second of inactivity
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [value, storageKey]);

  const loadDraft = useCallback(() => {
    if (!storageKey) return;
    
    const savedDraft = localStorage.getItem(`markdown-draft-${storageKey}`);
    if (savedDraft) {
      try {
        const { content } = JSON.parse(savedDraft);
        if (content) {
          onChange(content);
          setHasDraft(false);
          toast.success("Draft restored");
        }
      } catch (e) {
        toast.error("Failed to restore draft");
      }
    }
  }, [storageKey, onChange]);

  const clearDraft = useCallback(() => {
    if (!storageKey) return;
    
    localStorage.removeItem(`markdown-draft-${storageKey}`);
    setHasDraft(false);
    setLastSaved(null);
    toast.success("Draft cleared");
  }, [storageKey]);

  // Handle escape key for fullscreen
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isFullscreen]);

  // Prevent body scroll when fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isFullscreen]);

  // Update history when value changes (not from undo/redo)
  useEffect(() => {
    if (isUndoRedoRef.current) {
      isUndoRedoRef.current = false;
      return;
    }
    
    // Don't add to history if value is same as current
    if (history[historyIndex] === value) return;
    
    // Truncate future history and add new state
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(value);
    
    // Limit history to 50 states
    if (newHistory.length > 50) {
      newHistory.shift();
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    } else {
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  }, [value]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const undo = useCallback(() => {
    if (!canUndo) return;
    isUndoRedoRef.current = true;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    onChange(history[newIndex]);
  }, [canUndo, historyIndex, history, onChange]);

  const redo = useCallback(() => {
    if (!canRedo) return;
    isUndoRedoRef.current = true;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    onChange(history[newIndex]);
  }, [canRedo, historyIndex, history, onChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCodeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const insertMarkdown = useCallback((prefix: string, suffix: string = "", placeholder: string = "") => {
    const textarea = document.getElementById(id || "markdown-editor") as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end) || placeholder;
    const newText = value.substring(0, start) + prefix + selectedText + suffix + value.substring(end);
    
    onChange(newText);
    
    setTimeout(() => {
      textarea.focus();
      const cursorPos = start + prefix.length + selectedText.length + suffix.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  }, [id, value, onChange]);

  // Keyboard shortcuts handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!(e.ctrlKey || e.metaKey)) return;

    // Handle undo/redo
    if (e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) {
        redo();
      } else {
        undo();
      }
      return;
    }

    // Handle redo with Ctrl+Y as alternative
    if (e.key.toLowerCase() === 'y') {
      e.preventDefault();
      redo();
      return;
    }

    // Handle find/replace with Ctrl+H
    if (e.key.toLowerCase() === 'h') {
      e.preventDefault();
      setShowFindReplace(true);
      return;
    }

    // Handle find with Ctrl+F
    if (e.key.toLowerCase() === 'f') {
      e.preventDefault();
      setShowFindReplace(true);
      return;
    }

    const shortcuts: Record<string, () => void> = {
      'b': () => insertMarkdown("**", "**", "bold text"),
      'i': () => insertMarkdown("*", "*", "italic text"),
      'k': () => insertMarkdown("[", "](url)", "link text"),
      '`': () => insertMarkdown("`", "`", "code"),
      'e': () => insertMarkdown("`", "`", "code"),
    };

    const key = e.key.toLowerCase();
    if (shortcuts[key]) {
      e.preventDefault();
      shortcuts[key]();
    }
  }, [insertMarkdown, undo, redo]);

  const insertCodeBlock = (language: string) => {
    insertMarkdown(`\n\`\`\`${language}\n`, "\n```\n", "// your code here");
    setShowCodeDropdown(false);
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return null;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return null;
    }

    const fileExt = file.name.split(".").pop()?.toLowerCase();
    const fileName = `markdown-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `images/${fileName}`;

    const result = await uploadFile(file, filePath);

    if (!result.success) {
      if (result.error === 'SESSION_EXPIRED') {
        // Session expiry is already handled by the hook
        return null;
      }
      console.error("Upload error:", result.error);
      toast.error("Failed to upload image");
      return null;
    }

    return result.url || null;
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setLocalIsUploading(true);
    const uploadPromises: Promise<void>[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith("image/")) {
        uploadPromises.push(
          uploadImage(file).then((url) => {
            if (url) {
              const imageMarkdown = `![${file.name}](${url})`;
              onChange(value + (value ? "\n" : "") + imageMarkdown);
            }
          })
        );
      }
    }

    await Promise.all(uploadPromises);
    setLocalIsUploading(false);
    
    if (uploadPromises.length > 0) {
      toast.success(`Uploaded ${uploadPromises.length} image(s)`);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (activeTab === "write") {
      setIsDragging(true);
    }
  }, [activeTab]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (activeTab !== "write") return;

    const files = e.dataTransfer.files;
    await handleImageUpload(files);
  }, [activeTab, value, onChange]);

  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageItems: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        const file = items[i].getAsFile();
        if (file) imageItems.push(file);
      }
    }

    if (imageItems.length > 0) {
      e.preventDefault();
      setLocalIsUploading(true);
      for (const file of imageItems) {
        const url = await uploadImage(file);
        if (url) {
          const imageMarkdown = `![Pasted image](${url})`;
          onChange(value + (value ? "\n" : "") + imageMarkdown);
        }
      }
      setLocalIsUploading(false);
      toast.success(`Uploaded ${imageItems.length} image(s)`);
    }
  }, [value, onChange]);

  const toolbarButtons = [
    { icon: Bold, label: "Bold", action: () => insertMarkdown("**", "**", "bold text") },
    { icon: Italic, label: "Italic", action: () => insertMarkdown("*", "*", "italic text") },
    { icon: Heading1, label: "Heading 1", action: () => insertMarkdown("# ", "", "Heading") },
    { icon: Heading2, label: "Heading 2", action: () => insertMarkdown("## ", "", "Heading") },
    { icon: List, label: "Bullet List", action: () => insertMarkdown("- ", "", "list item") },
    { icon: ListOrdered, label: "Numbered List", action: () => insertMarkdown("1. ", "", "list item") },
    { icon: Code, label: "Inline Code", action: () => insertMarkdown("`", "`", "code") },
    { icon: Link2, label: "Link", action: () => insertMarkdown("[", "](url)", "link text") },
    { icon: Quote, label: "Quote", action: () => insertMarkdown("> ", "", "quote") },
    { icon: Minus, label: "Divider", action: () => insertMarkdown("\n---\n", "", "") },
  ];

  return (
    <>
      {/* Fullscreen backdrop */}
      {isFullscreen && (
        <div 
          className="fixed inset-0 bg-background/95 backdrop-blur-sm z-40"
          onClick={() => setIsFullscreen(false)}
        />
      )}
      
      <div 
        className={cn(
          "border border-border rounded-lg overflow-hidden bg-background",
          isFullscreen && "fixed inset-4 z-50 flex flex-col shadow-2xl"
        )}
        ref={editorRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        multiple
        onChange={(e) => handleImageUpload(e.target.files)}
      />

      {/* Toolbar */}
      <div className="flex items-center justify-between bg-secondary/30 border-b border-border px-2 py-1.5">
        <div className="flex items-center gap-0.5">
          {/* Undo/Redo buttons */}
          <button
            type="button"
            onClick={undo}
            className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Undo (Ctrl+Z)"
            disabled={activeTab === "preview" || !canUndo}
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={redo}
            className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Redo (Ctrl+Shift+Z)"
            disabled={activeTab === "preview" || !canRedo}
          >
            <Redo2 className="w-4 h-4" />
          </button>
          
          <div className="w-px h-4 bg-border mx-1" />
          
          {toolbarButtons.map((btn, index) => (
            <button
              key={index}
              type="button"
              onClick={btn.action}
              className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              title={btn.label}
              disabled={activeTab === "preview"}
            >
              <btn.icon className="w-4 h-4" />
            </button>
          ))}
          
          {/* Code Block Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setShowCodeDropdown(!showCodeDropdown)}
              className={cn(
                "flex items-center gap-0.5 p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors",
                showCodeDropdown && "bg-secondary text-foreground"
              )}
              title="Insert Code Block"
              disabled={activeTab === "preview"}
            >
              <FileCode className="w-4 h-4" />
              <ChevronDown className="w-3 h-3" />
            </button>
            
            {showCodeDropdown && (
              <div className="absolute top-full left-0 mt-1 w-40 bg-background border border-border rounded-lg shadow-lg z-50 py-1 max-h-64 overflow-y-auto">
                {codeLanguages.map((lang) => (
                  <button
                    key={lang.value}
                    type="button"
                    onClick={() => insertCodeBlock(lang.value)}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary transition-colors"
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Table Grid Selector */}
          <div className="relative" ref={tableGridRef}>
            <button
              type="button"
              onClick={() => setShowTableGrid(!showTableGrid)}
              className={cn(
                "p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors",
                showTableGrid && "bg-secondary text-foreground"
              )}
              title="Insert Table"
              disabled={activeTab === "preview"}
            >
              <Table className="w-4 h-4" />
            </button>
            
            {showTableGrid && (
              <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-50 p-3">
                <p className="text-xs text-muted-foreground mb-2">
                  {tableGridHover.rows > 0 && tableGridHover.cols > 0 
                    ? `${tableGridHover.rows} × ${tableGridHover.cols}` 
                    : "Select size"}
                </p>
                <div 
                  className="grid gap-1"
                  style={{ gridTemplateColumns: "repeat(6, 1fr)" }}
                  onMouseLeave={() => setTableGridHover({ rows: 0, cols: 0 })}
                >
                  {Array.from({ length: 6 }).map((_, rowIndex) =>
                    Array.from({ length: 6 }).map((_, colIndex) => (
                      <button
                        key={`${rowIndex}-${colIndex}`}
                        type="button"
                        className={cn(
                          "w-5 h-5 border rounded transition-colors",
                          rowIndex < tableGridHover.rows && colIndex < tableGridHover.cols
                            ? "bg-primary border-primary"
                            : "bg-secondary/50 border-border hover:border-primary/50"
                        )}
                        onMouseEnter={() => setTableGridHover({ rows: rowIndex + 1, cols: colIndex + 1 })}
                        onClick={() => insertTable(rowIndex + 1, colIndex + 1)}
                      />
                    ))
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2 text-center">
                  Max 6×6
                </p>
              </div>
            )}
          </div>

          {/* Emoji Picker */}
          <div className="relative" ref={emojiPickerRef}>
            <button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className={cn(
                "p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors",
                showEmojiPicker && "bg-secondary text-foreground"
              )}
              title="Insert Emoji"
              disabled={activeTab === "preview"}
            >
              <Smile className="w-4 h-4" />
            </button>
            
            {showEmojiPicker && (
              <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-50 p-2 w-64">
                {/* Category tabs */}
                <div className="flex gap-1 mb-2 border-b border-border pb-2">
                  {emojiCategories.map((cat, index) => (
                    <button
                      key={cat.name}
                      type="button"
                      onClick={() => setEmojiCategory(index)}
                      className={cn(
                        "px-2 py-1 text-xs rounded transition-colors",
                        emojiCategory === index
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-secondary text-muted-foreground"
                      )}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
                
                {/* Emoji grid */}
                <div className="grid grid-cols-8 gap-1 max-h-32 overflow-y-auto">
                  {emojiCategories[emojiCategory].emojis.map((emoji, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        insertEmoji(emoji);
                        setShowEmojiPicker(false);
                      }}
                      className="w-7 h-7 flex items-center justify-center text-lg hover:bg-secondary rounded transition-colors"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Image Upload Button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
            title="Upload Image"
            disabled={activeTab === "preview" || localIsUploading}
          >
            {localIsUploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ImagePlus className="w-4 h-4" />
            )}
          </button>
        </div>
        
        {/* Tab Switcher & Fullscreen */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-secondary rounded-md p-0.5">
            <button
              type="button"
              onClick={() => setActiveTab("write")}
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors",
                activeTab === "write" 
                  ? "bg-background text-foreground shadow-sm" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Edit3 className="w-3.5 h-3.5" />
              Write
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("preview")}
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors",
                activeTab === "preview" 
                  ? "bg-background text-foreground shadow-sm" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Eye className="w-3.5 h-3.5" />
              Preview
            </button>
          </div>
          
          {/* Fullscreen Toggle */}
          <button
            type="button"
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
            title={isFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen mode"}
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Find and Replace Panel */}
      {showFindReplace && (
        <div className="flex flex-col gap-2 px-3 py-2 bg-secondary/30 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2">
              <Search className="w-4 h-4 text-muted-foreground shrink-0" />
              <Input
                ref={findInputRef}
                type="text"
                value={findText}
                onChange={(e) => setFindText(e.target.value)}
                placeholder="Find..."
                className="h-7 text-sm"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (e.shiftKey) {
                      findPrevious();
                    } else {
                      findNext();
                    }
                  }
                  if (e.key === "Escape") {
                    setShowFindReplace(false);
                  }
                }}
              />
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {matchCount > 0 ? `${currentMatchIndex + 1}/${matchCount}` : "0/0"}
              </span>
              <button
                type="button"
                onClick={findPrevious}
                disabled={matchCount === 0}
                className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                title="Previous (Shift+Enter)"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={findNext}
                disabled={matchCount === 0}
                className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                title="Next (Enter)"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowFindReplace(false);
                setFindText("");
                setReplaceText("");
              }}
              className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              title="Close (Esc)"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2">
              <Replace className="w-4 h-4 text-muted-foreground shrink-0" />
              <Input
                type="text"
                value={replaceText}
                onChange={(e) => setReplaceText(e.target.value)}
                placeholder="Replace with..."
                className="h-7 text-sm"
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setShowFindReplace(false);
                  }
                }}
              />
            </div>
            <button
              type="button"
              onClick={replaceCurrent}
              disabled={matchCount === 0}
              className="px-2 py-1 text-xs rounded bg-secondary hover:bg-secondary/80 text-foreground transition-colors disabled:opacity-40"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={replaceAll}
              disabled={matchCount === 0}
              className="px-2 py-1 text-xs rounded bg-primary hover:bg-primary/90 text-primary-foreground transition-colors disabled:opacity-40"
            >
              Replace All
            </button>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className={cn(
        "min-h-[150px] relative",
        isFullscreen && "flex-1 overflow-hidden"
      )}>
        {activeTab === "write" ? (
          <>
            <Textarea
              id={id || "markdown-editor"}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              rows={isFullscreen ? undefined : rows}
              className={cn(
                "border-0 rounded-none focus-visible:ring-0 resize-none",
                isFullscreen && "h-full min-h-full"
              )}
            />
            
            {/* Drag overlay */}
            {isDragging && (
              <div className="absolute inset-0 bg-primary/10 border-2 border-dashed border-primary rounded-lg flex items-center justify-center z-10">
                <div className="flex flex-col items-center gap-2 text-primary">
                  <Upload className="w-8 h-8" />
                  <span className="font-medium">Drop images here</span>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={cn(
            "p-4 min-h-[150px] overflow-auto",
            isFullscreen && "h-full"
          )}>
            {value ? (
              <MarkdownRenderer content={value} />
            ) : (
              <p className="text-muted-foreground text-sm italic">Nothing to preview</p>
            )}
          </div>
        )}
      </div>

      {/* Draft notification banner */}
      {hasDraft && storageKey && (
        <div className="px-3 py-2 bg-amber-500/10 border-t border-amber-500/20 flex items-center justify-between gap-2">
          <p className="text-xs text-amber-600 dark:text-amber-400">
            You have an unsaved draft from a previous session.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadDraft}
              className="text-xs px-2 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-700 dark:text-amber-300 transition-colors"
            >
              Restore
            </button>
            <button
              type="button"
              onClick={clearDraft}
              className="text-xs px-2 py-1 rounded hover:bg-secondary text-muted-foreground transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Helper Text & Stats */}
      <div className="px-3 py-1.5 bg-secondary/20 border-t border-border flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <p className="text-[10px] text-muted-foreground">
            Markdown supported. Shortcuts: Ctrl+Z undo, Ctrl+B bold, Ctrl+I italic.
          </p>
          {storageKey && lastSaved && (
            <span className="text-[10px] text-muted-foreground/70 flex items-center gap-1">
              <Save className="w-3 h-3" />
              Saved
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground shrink-0">
          {storageKey && (
            <button
              type="button"
              onClick={clearDraft}
              className="hover:text-destructive transition-colors flex items-center gap-1"
              title="Clear draft"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
          <span>{value.trim() ? value.trim().split(/\s+/).length : 0} words</span>
          <span>{value.length} chars</span>
        </div>
      </div>
    </div>
    </>
  );
};
